import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import Pagination from '../../../shared/components/Pagination';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import { subjectAttendanceLink } from '../../../shared/constant/assessmentTabs';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { getAllSubjects, getOwnSectionGroups, getOwnSubjects } from '../../Subject/services/subjectService';
import { getDistinctSectionGroups } from '../../Student/services/studentService';
import { getAttendanceSummary, getAttendanceSummaryForAdmin } from '../services/attendanceSummaryService';
import StudentAttendanceModal, { STATUSES } from './StudentAttendanceModal';

const PAGE_SIZE = 10;

const DAY_MODE = 'day';
const MONTH_MODE = 'month';

// en-CA formats as YYYY-MM-DD in the user's local time, which is what a date input expects.
const todayString = () => new Date().toLocaleDateString('en-CA');
const currentMonthString = () => todayString().slice(0, 7);

// Inclusive first/last day of `month` (YYYY-MM), in local time.
function monthRange(month) {
  const [year, monthNum] = month.split('-').map(Number);
  const lastDay = new Date(year, monthNum, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
}

const sectionKey = (g) => `${g.grade_level}|${g.section}`;
const studentName = (s) => `${s.lastname}, ${s.firstname} ${s.middlename ? `${s.middlename} ` : ''}`;

function AttendanceSummaryPage() {
  const { role } = useAuth();
  // Admin and super admin aren't teachers, so they see every section/subject school-wide instead
  // of "their own" (which only means something for a teacher).
  const isSuperAdmin = role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN;

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');
  const [subjects, setSubjects] = useState([]);
  // Links from elsewhere (e.g. the dashboard's attention list) can pick the section and view to open on.
  const [searchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState(searchParams.get('section') ?? '');
  const [subjectName, setSubjectName] = useState('');
  const [mode, setMode] = useState(searchParams.get('mode') === MONTH_MODE ? MONTH_MODE : DAY_MODE);
  const [date, setDate] = useState(todayString());
  const [month, setMonth] = useState(currentMonthString());
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Index into `rows` of the student whose attendance modal is open.
  const [viewIndex, setViewIndex] = useState(null);

  useEffect(() => {
    let mounted = true;
    (isSuperAdmin ? getDistinctSectionGroups() : getOwnSectionGroups())
      .then((result) => {
        if (mounted) setGroups(result);
      })
      .catch((err) => {
        if (mounted) setGroupsError(err.message || 'Failed to load sections');
      })
      .finally(() => {
        if (mounted) setGroupsLoading(false);
      });

    (isSuperAdmin ? getAllSubjects() : getOwnSubjects())
      .then((result) => {
        if (mounted) setSubjects(result);
      })
      .catch(() => {
        // The subject filter just stays empty; the section filter and totals still work.
      });

    return () => {
      mounted = false;
    };
  }, [isSuperAdmin]);

  // A section from the URL that isn't one of the user's falls back to all sections.
  useEffect(() => {
    if (!groupsLoading && selectedKey && !groups.some((g) => sectionKey(g) === selectedKey)) setSelectedKey('');
  }, [groupsLoading, groups, selectedKey]);

  const selectedGroups = selectedKey ? groups.filter((g) => sectionKey(g) === selectedKey) : groups;

  // Only offer subjects actually taught to one of the sections currently in view, deduplicated by
  // name — the same subject (e.g. "English") is often a separate row per grade level.
  const subjectOptions = [
    ...new Map(
      subjects
        .filter((s) =>
          selectedGroups.some((g) => g.grade_level === s.grade_level && s.subject_sections.some((ss) => ss.section === g.section))
        )
        .map((s) => [s.name.toLowerCase(), s.name])
    ).values(),
  ].sort((a, b) => a.localeCompare(b));

  const handleSectionChange = (key) => {
    setSelectedKey(key);
    setSubjectName('');
  };

  const { from, to } = mode === DAY_MODE ? { from: date, to: date } : monthRange(month);

  useEffect(() => {
    if (groupsLoading) return;
    let mounted = true;
    setLoading(true);
    setError('');
    setPage(1);

    (isSuperAdmin ? getAttendanceSummaryForAdmin : getAttendanceSummary)({
      groups: selectedGroups,
      from,
      to,
      subjectName,
    })
      .then((result) => {
        if (mounted) setRows(result);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load attendance');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
    // selectedGroups is recomputed from groups + selectedKey every render, so depending on
    // those two instead avoids re-fetching on every render from a new array identity.
  }, [groupsLoading, selectedKey, groups, from, to, isSuperAdmin, subjectName]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(
    () =>
      STATUSES.reduce((acc, s) => {
        acc[s.value] = rows.reduce((sum, r) => sum + r[s.value], 0);
        return acc;
      }, {}),
    [rows]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Previous/Next in the modal can step past this page, so keep the table on the same page.
  const handleViewIndexChange = useCallback((i) => {
    setViewIndex(i);
    setPage(Math.floor(i / PAGE_SIZE) + 1);
  }, []);

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-4/5 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Attendance Summary</h1>
        </div>

        {groupsError ? (
          <p className="shrink-0 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{groupsError}</p>
        ) : !groupsLoading && groups.length === 0 ? (
          <div className="shrink-0 bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            {isSuperAdmin
              ? 'No student sections found yet.'
              : 'No sections are assigned to you yet. Ask an admin to assign a section to one of your subjects.'}
          </div>
        ) : (
          <>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex gap-2 w-full sm:w-auto">
                <select
                  value={selectedKey}
                  onChange={(e) => handleSectionChange(e.target.value)}
                  disabled={groupsLoading}
                  aria-label="Filter by section"
                  className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] disabled:opacity-50"
                >
                  <option value="">{isSuperAdmin ? 'All Sections' : 'All My Sections'}</option>
                  {groups.map((g) => (
                    <option key={sectionKey(g)} value={sectionKey(g)}>
                      {getGradeLevelLabel(g.grade_level)} - {g.section}
                    </option>
                  ))}
                </select>

                <select
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                  disabled={groupsLoading}
                  aria-label="Filter by subject"
                  className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] disabled:opacity-50"
                >
                  <option value="">All Subjects</option>
                  {subjectOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <div className="flex flex-1 sm:flex-none rounded-md border border-[#4ea6d0] overflow-hidden text-sm">
                  {[
                    { value: DAY_MODE, label: 'Day' },
                    { value: MONTH_MODE, label: 'Month' },
                  ].map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMode(m.value)}
                      aria-pressed={mode === m.value}
                      className={`flex-1 sm:flex-none px-4 py-2 font-medium transition ${
                        mode === m.value ? 'bg-[#0b4a71] text-white' : 'text-[#0b4a71] hover:bg-[#4ea6d0]/10'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {mode === DAY_MODE ? (
                  <input
                    type="date"
                    value={date}
                    max={todayString()}
                    onChange={(e) => setDate(e.target.value)}
                    aria-label="Date"
                    className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                  />
                ) : (
                  <input
                    type="month"
                    value={month}
                    max={currentMonthString()}
                    onChange={(e) => setMonth(e.target.value)}
                    aria-label="Month"
                    className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                  />
                )}
              </div>
            </div>

            <div className="shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {STATUSES.map((s) => (
                <div key={s.value} className="flex items-center gap-3 bg-white rounded-lg p-3 sm:p-4">
                  <span className={`h-3 w-3 rounded-full shrink-0`} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-xs sm:text-sm text-gray-500">{s.label}</p>
                    <p className={`text-2xl sm:text-3xl font-bold leading-tight ${s.text}`}>
                      {loading ? '—' : totals[s.value]}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
              {error ? (
                <p className="text-center text-red-600 py-12">{error}</p>
              ) : loading || groupsLoading ? (
                <p className="text-center text-gray-500 py-12">Loading…</p>
              ) : rows.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No students found in the selected section.</p>
              ) : (
                <>
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Section</th>
                          {STATUSES.map((s) => (
                            <th key={s.value} className="px-4 py-3 font-medium text-center" title={s.label}>
                              {s.abbr}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagedRows.map((student, i) => (
                          <tr
                            key={student.id}
                            onClick={() => setViewIndex((page - 1) * PAGE_SIZE + i)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setViewIndex((page - 1) * PAGE_SIZE + i);
                              }
                            }}
                            tabIndex={0}
                            aria-label={`View attendance of ${studentName(student).trim()}`}
                            className="cursor-pointer hover:bg-[#4ea6d0]/20 focus:outline-none focus-visible:bg-[#4ea6d0]/20"
                          >
                            <td className="px-4 py-3 font-medium text-[#0b4a71]">{studentName(student)}</td>
                            <td className="px-4 py-3 text-gray-600">{student.section || '—'}</td>
                            {STATUSES.map((s) => (
                              <td key={s.value} className={`px-4 py-3 text-center font-medium ${s.text}`}>
                                {student[s.value]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    totalItems={rows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                  />
                </>
              )}
            </div>
          </>
        )}
      </main>

      {viewIndex !== null && rows[viewIndex] && (
        <StudentAttendanceModal
          students={rows}
          index={viewIndex}
          subjects={subjects}
          subjectName={subjectName}
          // Teachers' subjects here are their own, so each opens its subject page; admins have none.
          subjectLink={
            isSuperAdmin ? null : (subjectId, student) => subjectAttendanceLink(subjectId, student.section)
          }
          onIndexChange={handleViewIndexChange}
          onClose={() => setViewIndex(null)}
        />
      )}
    </div>
  );
}

export default AttendanceSummaryPage;
